import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { UserRole } from '@workflow/shared-types'

interface User {
  id: string
  email: string
  full_name: string | null
  role: UserRole
  org_id: string
  timezone: string
}

interface AuthState {
  user: User | null
  /** In-memory JWT only — never persisted (see `partialize`). Mirrors the module-level token. */
  accessToken: string | null
  isAuthenticated: boolean
  viewTimezone: string
  _hasHydrated: boolean
  setUser: (user: User) => void
  setTokens: (accessToken: string) => void
  setViewTimezone: (tz: string) => void
  logout: () => void
}

// Module-level memory-only token: the single source of truth for the JWT.
// Deliberately NOT in localStorage/sessionStorage (XSS-safe). The refresh
// token lives in an HttpOnly cookie; session restore goes through /auth/refresh.
let memoryAccessToken: string | null = null

interface StoredSession {
  user: User | null
  isAuthenticated?: boolean
  viewTimezone?: string
}

// Synchronously seed initial state from storage. Rationale: zustand's async
// rehydration was observed to never settle in some environments (blank app
// with zero errors — PrivateRoute gates on `_hasHydrated` forever). Seeding
// makes boot deterministic: what persist would merge, we apply up-front;
// persist's own rehydrate then becomes a harmless no-op reaffirmation.
// Storage access is guarded (blocked storage => clean logged-out boot).
function readStoredSession(): StoredSession | null {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return null
    const raw = window.localStorage.getItem('auth-storage')
    if (!raw) return null
    const parsed = JSON.parse(raw) as { state?: StoredSession }
    return parsed?.state ?? null
  } catch {
    return null
  }
}

const seededSession = readStoredSession()

export function getAccessToken(): string | null {
  return memoryAccessToken
}

export function setAccessToken(token: string | null): void {
  memoryAccessToken = token
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      user: seededSession?.user ?? null,
      accessToken: null,
      // Seeded synchronously above; the store is therefore usable from the
      // very first render — no blank-screen gate on async rehydration.
      isAuthenticated: seededSession?.isAuthenticated ?? false,
      viewTimezone:
        seededSession?.viewTimezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
      _hasHydrated: true,
      setUser: (user) => set({ user, isAuthenticated: true }),
      // Atomic: module memory + observable store state stay in sync.
      setTokens: (accessToken) => {
        setAccessToken(accessToken)
        set({ accessToken })
      },
      setViewTimezone: (tz) => set({ viewTimezone: tz }),
      logout: () => {
        // Revoke the HttpOnly refresh cookie server-side; ignore failures (offline, etc.).
        try {
          const baseUrl = (import.meta as unknown as { env?: Record<string, string | undefined> }).env?.VITE_API_URL ?? '/api/v1'
          void fetch(`${baseUrl}/auth/logout`, {
            method: 'POST',
            credentials: 'include',
          }).catch(() => undefined)
        } catch {
          // ignore — local cleanup below still runs
        }
        setAccessToken(null)
        set({ user: null, accessToken: null, isAuthenticated: false })
      },
    }),
    {
      name: 'auth-storage',
      // Persist everything EXCEPT accessToken (memory-only).
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
        viewTimezone: state.viewTimezone,
      }),
      // NOTE: zustand calls this SYNCHRONOUSLY during create() (sync storage),
      // so it must RETURN the finisher — never touch `useAuthStore` here or
      // the module self-reference throws TDZ ("Cannot access before
      // initialization") and the whole app renders blank. The finisher runs
      // after rehydration, when the store binding is initialized. (Primary
      // hydration is the synchronous seed above; this just reaffirms it.)
      onRehydrateStorage: () => (_restoredState) => {
        useAuthStore.setState({ _hasHydrated: true })
      },
    }
  )
)
